import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { currentLanguage } from '../i18n';
import { errorMessage } from '../errors';
import { notifyChanged } from '../shell/changes';
import { Alert, Button, Checkbox, Dialog, Pill, Select, TextField, Textarea } from '../ui';

const STATUS_TONES = { sent: 'accent', accepted: 'green', revoked: 'neutral', expired: 'amber' };

/** Invite someone by e-mail, optionally sharing collections; the invitations sent, revocable. */
function InviteModal({ inventories, onClose }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [selected, setSelected] = useState([]);
  const [permission, setPermission] = useState('read');
  // The invitee's language, as the inviter guesses it: by default the inviter's own.
  const [locale, setLocale] = useState(currentLanguage);
  const [sent, setSent] = useState([]);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get('/invitations').then((r) => setSent(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await api.post('/invitations', { email, message, inventory_ids: selected, permission, locale });
      setNotice(t('invite.sent', { email }));
      setEmail('');
      setMessage('');
      setSelected([]);
      load();
      // The admin accounts page lists every invitation: it fetches again.
      notifyChanged('invitations');
    } catch (err) {
      setError(errorMessage(t, err, 'invite.sendError'));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    setError('');
    setNotice('');
    try {
      await api.delete(`/invitations/${id}`);
      notifyChanged('invitations');
    } catch (err) {
      setError(errorMessage(t, err, 'invite.revokeError'));
    }
    load();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('invite.title')}
      description={t('invite.intro')}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.close')}</Button>
          <Button type="submit" form="invite-form" loading={busy}>{t('invite.submit')}</Button>
        </>
      )}
    >
      <form id="invite-form" className="dialog-form" onSubmit={submit}>
        {notice && <Alert tone="success">{notice}</Alert>}
        {error && <Alert tone="danger">{error}</Alert>}
        <TextField label={t('auth.email')} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <Select label={t('invite.language')} hint={t('invite.languageHelp')} value={locale} onChange={(e) => setLocale(e.target.value)}>
          <option value="fr" lang="fr">{t('language.fr')}</option>
          <option value="en" lang="en">{t('language.en')}</option>
        </Select>
        <Textarea label={t('invite.message')} rows={3} value={message} onChange={(e) => setMessage(e.target.value)} />
        {inventories.length > 0 && (
          <fieldset className="choice-list">
            <legend className="ui-field__label">{t('invite.shareThese')}</legend>
            {inventories.map((inv) => (
              <Checkbox key={inv.id} label={inv.name} checked={selected.includes(inv.id)} onChange={() => toggle(inv.id)} />
            ))}
            {selected.length > 0 && (
              <Select label={t('share.permission')} value={permission} onChange={(e) => setPermission(e.target.value)}>
                <option value="read">{t('share.canView')}</option>
                <option value="edit">{t('share.canEdit')}</option>
              </Select>
            )}
          </fieldset>
        )}
      </form>
      {sent.length > 0 && (
        <section aria-labelledby="invite-sent" className="invite-sent">
          <h3 id="invite-sent" className="dialog-section-title">{t('invite.sentList')}</h3>
          <ul className="people-list">
            {sent.map((inv) => {
              const status = inv.status === 'sent' && inv.expired ? 'expired' : inv.status;
              return (
                <li key={inv.id} className="people-list__row">
                  <span className="people-list__who"><span className="people-list__email">{inv.email}</span></span>
                  <Pill tone={STATUS_TONES[status] || 'neutral'}>{t(`invite.status.${status}`, { defaultValue: inv.status })}</Pill>
                  {inv.status === 'sent' && !inv.expired && (
                    <Button variant="ghost" size="sm" onClick={() => revoke(inv.id)}>{t('invite.revoke')}</Button>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </Dialog>
  );
}

export default InviteModal;
